import React from 'react'
import {
  ServerSettingsDialogProps,
  ServerSettingsMutationProps
} from './shared'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { updateDiagnosticCollectionSettings } from './update-diagnostic-collection-settings'
import { DiagnosticCollectionForm } from './diagnostic-collection-form'
import { DiagnosticCollectionFormValue } from './index'
import { useIntl } from 'react-intl'
import { DiagnosticCollectionLoader } from './diagnostic-collection-loader'

const diagnosticCollectionSettingsMutationArgMutationName =
  'updateDiagnosticCollectionSettings'

export const modifyDiagnosticCollectionSettingsMutationArgMutation = gql`
  mutation UpdateDiagnosticCollectionSettings(
    $organizationID: ID!
    $id: ID!
    $diagnosticCollection: DiagnosticCollectionSettingsArg!
  ) {
    ${diagnosticCollectionSettingsMutationArgMutationName} (
      input: { organizationID: $organizationID, id: $id, diagnosticCollection: $diagnosticCollection }
    ) {
      enabled
      logLevel
      duration
    }
  }
`

interface MutationData {
  readonly __typename: 'Mutation'
  readonly [diagnosticCollectionSettingsMutationArgMutationName]: {
    readonly _placeholder: true
    readonly __typename: 'DiagnosticCollectionSettingsMutationArg'
  }
}

interface MutationVariables {
  readonly diagnosticCollection: DiagnosticCollectionFormValue
  readonly organizationID: string
  readonly id: string
}

export function ModifyDiagnosticCollectionDialog({
  organizationID,
  serverID,
  refetchQueries,
  refresh,
  closeDialog,
  isOpen,
  isReadOnly
}: ServerSettingsDialogProps &
  ServerSettingsMutationProps & {
    serverID: string
    isReadOnly?: boolean
  }) {
  const { formatMessage } = useIntl()
  const errorMessage = 'detail_servers_errorSavingDiagnosticCollectionSettings'

  return (
    <DiagnosticCollectionLoader
      organizationID={organizationID}
      serverID={serverID}
    >
      {({ loading, diagnosticCollectionSettings, refetch }) => {
        return (
          !loading && (
            <Mutation<MutationData, MutationVariables>
              mutation={modifyDiagnosticCollectionSettingsMutationArgMutation}
              ignoreResults
            >
              {(mutate) => {
                return (
                  <DiagnosticCollectionForm
                    isReadOnly={isReadOnly}
                    diagnosticCollectionSettings={diagnosticCollectionSettings}
                    onConfirm={(formValue) => {
                      mutate({
                        refetchQueries,
                        variables: {
                          diagnosticCollection:
                            updateDiagnosticCollectionSettings(formValue),
                          organizationID,
                          id: serverID
                        },
                        optimisticResponse: {
                          [diagnosticCollectionSettingsMutationArgMutationName]:
                            {
                              _placeholder: true,
                              __typename:
                                'DiagnosticCollectionSettingsMutationArg'
                            },
                          __typename: 'Mutation'
                        },
                        update: () => false
                      })
                        .then(() => {
                          toast(
                            formatMessage({
                              id: diagnosticCollectionSettings.enabled
                                ? 'detail_servers_successfullyStoppedDiagnosticCollectionSettings'
                                : 'detail_servers_successfullySavedDiagnosticCollectionSettings'
                            })
                          )
                          refresh().catch((error) => {
                            toast(formatMessage({ id: errorMessage }), {
                              variant: 'error'
                            })
                            return error
                          })
                          refetch()
                        })
                        .catch((error) => {
                          toast(formatMessage({ id: errorMessage }), {
                            variant: 'error'
                          })
                          return error
                        })
                      closeDialog()
                    }}
                    onCancel={closeDialog}
                    isOpen={isOpen}
                  />
                )
              }}
            </Mutation>
          )
        )
      }}
    </DiagnosticCollectionLoader>
  )
}
